import React, { useEffect, useMemo, useState } from 'react';
import './ProjectConfiguration.scss';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import type { AdminAccountOption } from '@brand24-original/llm-types';
import { useAccount } from '../../../contexts/account/AccountContexts';
import { useNavigate } from 'react-router-dom';
import { PROJECTS } from '../../../routes/path';
import { changeProjectOwner, setProjectDemo } from '../../../services/projects/ProjectService';

interface ChangeProjectProps {
  allAccounts: AdminAccountOption[];
  ownerAccountId: number;
  demoProject: string | null;
  projectId: number;
  onSave: () => void;
}

interface AccountOption {
  name: string;
  id: number;
}

interface DemoOption {
  name: string;
  type: string;
}

const ProjectConfiguration: React.FC<ChangeProjectProps> = ({
  allAccounts,
  ownerAccountId,
  demoProject,
  projectId,
  onSave,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountResponse } = useAccount();

  const accountsOptions: AccountOption[] = useMemo(
    () =>
      allAccounts
        .map((account) => ({ name: account.email, id: account.accountId }))
        .sort((a, b) => a.id - b.id),
    [allAccounts]
  );

  const demoOptions: DemoOption[] = useMemo(
    () => [
      { name: 'US', type: 'US' },
      { name: 'PL', type: 'PL' },
    ],
    []
  );

  const currentAccount = useMemo(
    () => accountsOptions.find((account) => account.id === ownerAccountId) || null,
    [accountsOptions, ownerAccountId]
  );

  const [accountItem, setAccountItem] = useState<AccountOption | null>(currentAccount);
  const [demoItem, setDemoItem] = useState<DemoOption | null>(null);

  useEffect(() => {
    if (!accountResponse?.isSuperAdmin) {
      navigate(PROJECTS);
    }
  }, [accountResponse, navigate]);

  const changeOwner = () => {
    if (!accountItem || accountItem.id === ownerAccountId) {
      return;
    }

    changeProjectOwner(projectId, accountItem.id).then((data) => {
      if (data) {
        onSave();
      }
    });
  };

  const changeDemo = (type: string | null) => {
    setProjectDemo(projectId, type).then((data) => {
      if (data) {
        onSave();
      }
    });
  };

  const accountItemTemplate = (option: AccountOption) => (
    <div className="p-dropdown-item">
      {option.name} (id: {option.id})
    </div>
  );
  const demoItemTemplate = (option: DemoOption) => <div className="p-dropdown-item">{option.name}</div>;

  return (
    <div className="project-configuration">
      <div className="project-configuration__row">
        <label>{t('projectAccountDesc')}</label>
        <Dropdown
          value={accountItem}
          onChange={(e) => setAccountItem(e.value)}
          options={accountsOptions}
          className="dropdown"
          optionLabel="name"
          itemTemplate={accountItemTemplate}
          placeholder={t('selectAccount')}
          filter
        />
        <Button className="btn btn--sm" label={t('save')} onClick={() => changeOwner()} />
      </div>

      {ownerAccountId === 1 && (
        <div className="project-configuration__row">
          <label>{t('demoDesc')}</label>
          {demoProject && (
            <Button
              className="btn btn--sm"
              label={`${t('unsetDemo')}  *${demoProject}*`}
              onClick={() => changeDemo(null)}
            />
          )}
          {ownerAccountId === 1 && !demoProject && (
            <>
              <Dropdown
                value={demoItem}
                onChange={(e) => setDemoItem(e.value)}
                options={demoOptions}
                className="dropdown"
                optionLabel="name"
                itemTemplate={demoItemTemplate}
                placeholder={t('selectDemoType')}
              />
              <Button
                className="btn btn--sm"
                label={t('setAsDemo')}
                onClick={() => {
                  if (demoItem) {
                    changeDemo(demoItem.type);
                  }
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectConfiguration;
